// test-table-grading — the PURE decision logic behind the test table that can be attached to an
// assignment, a challenge or a guided make (activity-test-table, Trello 2P8y4y9S). No DOM, no
// network, no runner: every function here is a plain transformation (the one orchestrator,
// `runSuite`, takes its runners as injected functions), so it is unit-testable directly under
// Node (the host-only ESM spec `test/editor/test-table-grading.test.js`). The chrome
// (`test-table-chrome.js`) owns the runners and the widget; this module owns every RULE.
//
// ── The rules, in one place ───────────────────────────────────────────────────────────────
//
//   1. LITERALS are parsed by `ast.literal_eval` inside Pyodide — never `eval`, never a JS
//      parser. The parse program is find-the-bug's (`buildParseProgram` / `readParseOutput`),
//      IMPORTED, never copied: there is one parsing program in the codebase.
//
//   2. SELF-CONSISTENCY (owner decision): a row's Pass/Fail compares the student's PREDICTION with
//      what THEIR OWN code does — never with the model answer. Nothing a student sees is ever
//      computed from the model answer's output, so the feedback can never leak a correct value.
//      An Invalid row passes when the student's own code RAISES (any exception).
//
//   3. MUTANT CATCHING is graded INDEPENDENTLY of the student's own code: a row catches a mutant
//      when, on that row's input, the mutant behaves differently from the model answer (both run
//      in the hidden runner). Expected / Boundary rows: any divergence — a different value, one
//      raising where the other does not, the mutant not finishing. Numeric values within
//      `boundaryTolerance` count as the SAME. Invalid rows: the row catches the mutant only when
//      the model answer RAISES and the mutant does NOT (tolerance plays no part). A row where the
//      model answer itself timed out or has no such function is unusable and catches nothing.
//
//   4. STICKY WITHIN A FINGERPRINT: the caught-set is the union of every run under the same
//      mutant fingerprint; a different fingerprint starts fresh (`stickyMutantsCaught`).
//
//   5. `minRows` is ADVISORY coverage feedback, `maxRows` is a HARD cap on adding rows, and a
//      duplicate-input row is flagged ADVISORY only — none of the three ever gates.
//
//   6. One runner call per (row × source) — never a batch (hidden-runner's own rule: one
//      non-terminating case must never sink another row or another mutant).

import {
  buildParseProgram, readParseOutput, observeFunctionResult, observeConsoleRun,
  matchesExpectation, formatObserved, coverage as ftbCoverage, ROW_TYPES, QUOTE_HINT
} from './find-the-bug-grading.js';
import { normalizeOutput } from './challenge-grading.js';

export { buildParseProgram, readParseOutput, observeFunctionResult, observeConsoleRun, formatObserved, ROW_TYPES, QUOTE_HINT };

// pgzero-test-tables (D4): GAME_ONLY — the call raised `_PcGameOnly` (it touched a Pygame Zero
// object that only exists while the game runs). Never a pass; widget label "Can't test".
export var OWN = {
  PASS: 'pass', FAIL: 'fail', TIMEOUT: 'timeout', BAD_LITERAL: 'bad-literal', GAME_ONLY: 'game-only', NOT_RUN: ''
};

// ── pgzero-test-tables (Trello TNUmFH7g) ──────────────────────────────────────────────────
//
//   7. A PYGAME ZERO source is never run as a game: `buildPgzeroWrapper` turns it into a console
//      program (run in the hidden python-mode runner) that keeps only the top-level defs, classes,
//      non-game imports and assignments — each compiled ALONE with its ORIGINAL line numbers —
//      and stubs every name Pygame Zero injects so any use raises `_PcGameOnly: <name>`.
//
//   8. OWN-FUNCTION mode: the student picks one of their own functions (`buildFunctionListProgram`
//      lists them). Self-consistency only; an Invalid row either expects an error (default) or a
//      predicted return value (`expectError === false`). Completeness (`ownCompleteFor`) is a
//      row-for-row mirror of the server's rule, proven over test/fixtures/own-complete-cases.json.

/** The exception type the wrapper raises for any use of a game-only object. */
export var GAME_ONLY_TYPE = '_PcGameOnly';

/** The Pygame Zero names the wrapper stubs (`Actor` is a real, subclassable stub class). */
export var PGZERO_STUB_NAMES = [
  'clock', 'music', 'tone', 'Actor', 'keyboard', 'animate', 'Rect', 'ZRect', 'images', 'sounds',
  'mouse', 'keys', 'keymods', 'exit', 'screen'
];

/** The Pygame Zero event hooks — never offered as a function to test. */
export var PGZERO_HOOKS = [
  'draw', 'update', 'on_key_down', 'on_key_up', 'on_mouse_down', 'on_mouse_up', 'on_mouse_move', 'on_music_end'
];

export var FUNCTION_LIST_SENTINEL = '@@PCTT-FNLIST@@';
export var MAX_FUNCTION_SOURCE = 4000;
var WIRE_MAX_NAME = 100;
var WIRE_MAX_PARAMS = 10;
var MAX_GAME_USES = 10;
var MAX_HIDDEN = 50;
var HIDDEN_REASONS = ['input', 'edge', 'imports'];

/** A Python string literal holding `text` (JSON escapes are all legal Python escapes). */
function pyStringLiteral(text) {
  return JSON.stringify(JSON.stringify(typeof text === 'string' ? text : ''));
}

/**
 * Turn a `main.py` into a console-Python program the hidden runner can `callFunction` against.
 * `opts.stubs` (default true): the pgzero stubs and the placeholders for names a dropped
 * `pygame` / `pgzrun` / `pgzero` import would bind. A python host's own-mode wrapper passes
 * `{ stubs: false }` — the same keep/drop and seed, no game names.
 *
 * Every kept statement runs in its OWN try, compiled with the module's own `__file__` (the
 * harness runs the wrapper as `/game/main.py` and reports a line only for frames from that file)
 * so a raise reports the student's ORIGINAL line; a failure binds the names it would have bound
 * to a placeholder and the wrapper carries on. Everything else (`pgzrun.go()`, top-level loops,
 * `if`, expression statements) is dropped. All internal names start `_pc_`.
 */
export function buildPgzeroWrapper(source, opts) {
  var stubs = !(opts && opts.stubs === false);
  // `opts.fileName` (default "main.py"): the name a traceback / the compile uses when the harness
  // gave no `__file__`; a rooms.py run passes "rooms.py".
  var fileName = opts && typeof opts.fileName === 'string' && opts.fileName ? opts.fileName : 'main.py';
  return [
    'import ast as _pc_ast',
    'import json as _pc_json',
    'import random as _pc_random',
    '_pc_src = _pc_json.loads(' + pyStringLiteral(source) + ')',
    '_pc_file = globals().get("__file__") or ' + JSON.stringify(fileName),
    '_pc_stubs = ' + (stubs ? 'True' : 'False'),
    '',
    'class _PcGameOnly(Exception):',
    '    pass',
    '',
    '_PcGameOnly.__module__ = "builtins"',
    '',
    'def _pc_raise(self, *a, **k):',
    '    raise _PcGameOnly(object.__getattribute__(self, "_pc_name"))',
    '',
    'class _PcStub(object):',
    '    def __init__(self, name):',
    '        object.__setattr__(self, "_pc_name", name)',
    '    def __getattr__(self, attr):',
    '        raise _PcGameOnly(object.__getattribute__(self, "_pc_name"))',
    '    def __setattr__(self, attr, value):',
    '        raise _PcGameOnly(object.__getattribute__(self, "_pc_name"))',
    // Hashing is a use too: `d[screen]` / `screen in some_set` raise `_PcGameOnly` instead of a
    // KeyError whose text reads "<exception str() failed>". The wrapper only ever stores stubs as
    // dict VALUES (globals()[name] = stub), never as keys, so it never hashes one itself.
    '    __hash__ = _pc_raise',
    '',
    // Printing / formatting a game object is a use of it too: `str(alien)`, `f"{screen}"` and
    // `repr(...)` all raise `_PcGameOnly`. The wrapper itself never str()s or repr()s a stub.
    'for _pc_d in ("__str__", "__repr__", "__format__",',
    '              "__call__", "__getitem__", "__setitem__", "__iter__", "__len__", "__contains__",',
    '              "__bool__", "__int__", "__float__", "__index__", "__neg__", "__pos__", "__abs__",',
    '              "__add__", "__radd__", "__sub__", "__rsub__", "__mul__", "__rmul__", "__truediv__",',
    '              "__rtruediv__", "__floordiv__", "__rfloordiv__", "__mod__", "__rmod__", "__pow__",',
    '              "__lt__", "__le__", "__gt__", "__ge__", "__eq__", "__ne__"):',
    '    setattr(_PcStub, _pc_d, _pc_raise)',
    '',
    'class _PcActor(object):',
    '    def __init__(self, *a, **k):',
    '        raise _PcGameOnly("Actor")',
    '',
    '_PcActor.__name__ = "Actor"',
    '_PcActor.__qualname__ = "Actor"',
    '',
    // An `import pgzero[.actor|.builtins]` placeholder: `.Actor` (and the `actor` / `builtins`
    // submodules) resolve, so `class Ship(pgzero.actor.Actor)` still defines; every other use
    // raises like any stub.
    'class _PcModule(_PcStub):',
    '    def __getattr__(self, attr):',
    '        if attr == "Actor":',
    '            return _PcActor',
    '        if attr in ("actor", "builtins"):',
    '            return _PcModule(object.__getattribute__(self, "_pc_name") + "." + attr)',
    '        raise _PcGameOnly(object.__getattribute__(self, "_pc_name"))',
    '',
    'if _pc_stubs:',
    '    for _pc_n in ' + JSON.stringify(PGZERO_STUB_NAMES) + ':',
    '        globals()[_pc_n] = _PcActor if _pc_n == "Actor" else _PcStub(_pc_n)',
    '',
    '_pc_random.seed(0)',
    '',
    'def _pc_is_game(name):',
    '    return bool(name) and name.split(".")[0] in ("pgzrun", "pgzero", "pygame")',
    '',
    'def _pc_targets(t, out):',
    '    if isinstance(t, _pc_ast.Name):',
    '        out.append(t.id)',
    '    elif isinstance(t, (_pc_ast.Tuple, _pc_ast.List)):',
    '        for _pc_e in t.elts:',
    '            _pc_targets(_pc_e, out)',
    '    elif isinstance(t, _pc_ast.Starred):',
    '        _pc_targets(t.value, out)',
    '',
    'def _pc_bound(node):',
    '    out = []',
    '    if isinstance(node, (_pc_ast.FunctionDef, _pc_ast.AsyncFunctionDef, _pc_ast.ClassDef)):',
    '        out.append(node.name)',
    '    elif isinstance(node, _pc_ast.Import):',
    '        for _pc_a in node.names:',
    '            out.append(_pc_a.asname or _pc_a.name.split(".")[0])',
    '    elif isinstance(node, _pc_ast.ImportFrom):',
    '        for _pc_a in node.names:',
    '            if _pc_a.name != "*":',
    '                out.append(_pc_a.asname or _pc_a.name)',
    '    elif isinstance(node, _pc_ast.Assign):',
    '        for _pc_t in node.targets:',
    '            _pc_targets(_pc_t, out)',
    '    elif isinstance(node, (_pc_ast.AnnAssign, _pc_ast.AugAssign)):',
    '        _pc_targets(node.target, out)',
    '    return out',
    '',
    'def _pc_placeholders(names):',
    '    for _pc_n in names:',
    '        globals()[_pc_n] = _PcStub(_pc_n)',
    '',
    // `from pgzero.actor import Actor` / `from pgzero.builtins import Actor [as A]` binds the
    // REAL stub class, so a subclass still defines; `from pgzero import actor|builtins [as m]`
    // binds a pgzero MODULE placeholder (so `class S(actor.Actor)` defines too); any other
    // imported game name is a stub.
    'def _pc_from_game(module, names):',
    '    _pc_pgz = module.split(".")[0] == "pgzero"',
    '    for _pc_a in names:',
    '        if _pc_a.name == "*":',
    '            continue',
    '        _pc_b = _pc_a.asname or _pc_a.name',
    '        if _pc_a.name == "Actor":',
    '            globals()[_pc_b] = _PcActor',
    '        elif _pc_pgz and _pc_a.name in ("actor", "builtins"):',
    '            globals()[_pc_b] = _PcModule(_pc_b)',
    '        else:',
    '            globals()[_pc_b] = _PcStub(_pc_b)',
    '',
    // `import pgzero.actor [as pa]`: a pgzero module placeholder (above); pygame / pgzrun stubs.
    'def _pc_import_game(names):',
    '    for _pc_a in names:',
    '        _pc_b = _pc_a.asname or _pc_a.name.split(".")[0]',
    '        globals()[_pc_b] = _PcModule(_pc_b) if _pc_a.name.split(".")[0] == "pgzero" else _PcStub(_pc_b)',
    '',
    '_pc_KEEP = (_pc_ast.FunctionDef, _pc_ast.AsyncFunctionDef, _pc_ast.ClassDef, _pc_ast.Import,',
    '            _pc_ast.ImportFrom, _pc_ast.Assign, _pc_ast.AnnAssign, _pc_ast.AugAssign)',
    '',
    'for _pc_node in _pc_ast.parse(_pc_src, _pc_file).body:',
    '    if not isinstance(_pc_node, _pc_KEEP):',
    '        continue',
    '    if isinstance(_pc_node, _pc_ast.Import):',
    '        _pc_game = [a for a in _pc_node.names if _pc_is_game(a.name)]',
    '        if _pc_game:',
    '            if _pc_stubs:',
    '                _pc_import_game(_pc_game)',
    '            _pc_rest = [a for a in _pc_node.names if not _pc_is_game(a.name)]',
    '            if not _pc_rest:',
    '                continue',
    '            _pc_node = _pc_ast.copy_location(_pc_ast.Import(names=_pc_rest), _pc_node)',
    '    elif isinstance(_pc_node, _pc_ast.ImportFrom) and not _pc_node.level and _pc_is_game(_pc_node.module):',
    '        if _pc_stubs:',
    '            _pc_from_game(_pc_node.module, _pc_node.names)',
    '        continue',
    '    try:',
    '        _pc_mod = _pc_ast.fix_missing_locations(_pc_ast.Module(body=[_pc_node], type_ignores=[]))',
    '        exec(compile(_pc_mod, _pc_file, "exec"), globals())',
    '    except Exception:',
    '        _pc_placeholders(_pc_bound(_pc_node))',
    ''
  ].join('\n');
}

/**
 * The program that LISTS the testable top-level functions of `main.py` (AST only — the student's
 * code is never executed). Prints the sentinel then `{ functions: [{ name, params, line, source }] }`
 * or `{ error: 'syntax', line }`. Excludes every function with no positional parameter and —
 * on a Pygame Zero host only (`opts.lang === 'pgzero'`) — the Pygame Zero hooks: a console-
 * Python `def update(score, n)` is an ordinary function and IS listed. A duplicated name lists
 * its LAST definition; `params` is every positional parameter (posonly + regular), defaulted or
 * not; `source` is ≤ 4000 chars.
 *
 * On a Pygame Zero host each function also carries `gameUses`: the game objects its body READS,
 * in first-appearance order, computed STATICALLY (owner report 2026-10-04 — the chooser warns as
 * soon as such a function is chosen). Game names are tracked over the top-level statements in
 * SOURCE ORDER, mirroring the wrapper: they start as the stub names; a `pgzero` / `pgzrun` /
 * `pygame` import (the wrapper's `_pc_is_game` rule) or an assignment whose value reads a game
 * name adds the names it binds; a non-game import, any other assignment, a `def` or a `class`
 * (even `class Ship(Actor)`) rebinds them, so they stop being game names (`keys = []` then
 * `keys + [a]` is fine). A function's own parameters and its local bindings (including `match`
 * captures; unless declared `global` there) never count. May over-warn on a branch never taken:
 * the row verdicts stay authoritative.
 *
 * HIDE ANALYSIS (own-function-rooms-picker; on by default, `opts.hide === false` turns it off):
 * a function that has a positional parameter but cannot be tested is NOT listed and is reported in
 * `hidden: [{ name, reason }]` instead, reason the first of `input` (it calls `input()`, directly or
 * through functions of the SAME file), `imports` (it calls, directly or through same-file
 * functions, a name it does not define that `opts.other` — `{ name, source }`, the OTHER file —
 * defines at top level, or an attribute of a name bound by `import <other stem>`), `edge` (no
 * ordering comparison and no modulo whose operands involve a parameter or a local derived from
 * one; nested def / lambda / class bodies do not count). A name is analysed by its LAST
 * definition. A function with no positional parameter or a hook is in neither list.
 */
export function buildFunctionListProgram(source, opts) {
  var pg = !!(opts && opts.lang === 'pgzero');
  var hooks = pg ? PGZERO_HOOKS : [];
  var hide = !(opts && opts.hide === false);
  var other = opts && opts.other && typeof opts.other.source === 'string' ? opts.other : null;
  var otherStem = other && typeof other.name === 'string' ? other.name.replace(/\.py$/, '') : '';
  return [
    'import ast, json',
    'src = json.loads(' + pyStringLiteral(source) + ')',
    'OTHER_SRC = ' + (other ? 'json.loads(' + pyStringLiteral(other.source) + ')' : 'None'),
    'OTHER_STEM = ' + JSON.stringify(otherStem),
    'HIDE = ' + (hide ? 'True' : 'False'),
    'HOOKS = set(' + JSON.stringify(hooks) + ')',
    'PG = ' + (pg ? 'True' : 'False'),
    'GAME = set(' + JSON.stringify(pg ? PGZERO_STUB_NAMES : []) + ')',
    'MATCH_BINDS = tuple(getattr(ast, c) for c in ("MatchAs", "MatchStar", "MatchMapping") if hasattr(ast, c))',
    '',
    'def is_game_mod(name):',
    '    return bool(name) and name.split(".")[0] in ("pgzrun", "pgzero", "pygame")',
    '',
    'def targets(t, out):',
    '    if isinstance(t, ast.Name):',
    '        out.append(t.id)',
    '    elif isinstance(t, (ast.Tuple, ast.List)):',
    '        for e in t.elts:',
    '            targets(e, out)',
    '    elif isinstance(t, ast.Starred):',
    '        targets(t.value, out)',
    '',
    'def reads_game(expr):',
    '    return expr is not None and any(isinstance(n, ast.Name) and isinstance(n.ctx, ast.Load) and n.id in GAME for n in ast.walk(expr))',
    '',
    // Source order, mirroring the wrapper (which runs each kept top-level statement AFTER the
    // stubs): a game import, or an assignment whose value reads a game name, ADDS the names it
    // binds; a non-game import, any other assignment, a `def` or a `class` (even
    // `class Ship(Actor)` — a real class) REBINDS them, so they stop being game names.
    'def collect_game(body):',
    '    for node in body:',
    '        add, drop = [], []',
    '        if isinstance(node, ast.Import):',
    '            for al in node.names:',
    '                (add if is_game_mod(al.name) else drop).append(al.asname or al.name.split(".")[0])',
    '        elif isinstance(node, ast.ImportFrom):',
    '            game = not node.level and is_game_mod(node.module)',
    '            for al in node.names:',
    '                if al.name != "*":',
    '                    (add if game else drop).append(al.asname or al.name)',
    '        elif isinstance(node, (ast.FunctionDef, ast.AsyncFunctionDef, ast.ClassDef)):',
    '            drop.append(node.name)',
    '        elif isinstance(node, ast.Assign):',
    '            for t in node.targets:',
    '                targets(t, add if reads_game(node.value) else drop)',
    '        elif isinstance(node, ast.AnnAssign) and node.value is not None:',
    '            targets(node.target, add if reads_game(node.value) else drop)',
    '        elif isinstance(node, ast.AugAssign):',
    // `keys += [1]` reads its own target: still a game name if that already was one.
    '            was = isinstance(node.target, ast.Name) and node.target.id in GAME',
    '            targets(node.target, add if was or reads_game(node.value) else drop)',
    '        GAME.difference_update(drop)',
    '        GAME.update(add)',
    '',
    // ── the hide analysis (own-function-rooms-picker, D5 / D17 / D21): AST only, never executed ──
    'SCOPES = (ast.FunctionDef, ast.AsyncFunctionDef, ast.Lambda, ast.ClassDef)',
    'OTHER_DEFS = set()',
    'if OTHER_SRC is not None:',
    '    try:',
    '        for _n in ast.parse(OTHER_SRC).body:',
    '            if isinstance(_n, (ast.FunctionDef, ast.AsyncFunctionDef)):',
    '                OTHER_DEFS.add(_n.name)',
    '    except Exception:',
    '        pass',
    '',
    // The function\'s own body: try / for / with / if / comprehension bodies, but never a nested
    // def / lambda / class (D21).
    'def own_nodes(fn):',
    '    stack = list(fn.body)',
    '    while stack:',
    '        n = stack.pop()',
    '        if isinstance(n, SCOPES):',
    '            continue',
    '        yield n',
    '        stack.extend(ast.iter_child_nodes(n))',
    '',
    'def sub_nodes(stmts):',
    '    stack = list(stmts)',
    '    while stack:',
    '        n = stack.pop()',
    '        if isinstance(n, SCOPES):',
    '            continue',
    '        yield n',
    '        stack.extend(ast.iter_child_nodes(n))',
    '',
    'def names_in(expr, names):',
    '    return expr is not None and any(isinstance(n, ast.Name) and n.id in names for n in ast.walk(expr))',
    '',
    'def param_names(fn):',
    '    a = fn.args',
    '    out = set(x.arg for x in getattr(a, "posonlyargs", []) + a.args + a.kwonlyargs)',
    '    if a.vararg:',
    '        out.add(a.vararg.arg)',
    '    if a.kwarg:',
    '        out.add(a.kwarg.arg)',
    '    return out',
    '',
    // A parameter, or a local assigned from an expression holding a parameter / tainted name.
    'def tainted_names(fn):',
    '    t = param_names(fn)',
    '    nodes = list(own_nodes(fn))',
    '    changed = True',
    '    while changed:',
    '        changed = False',
    '        for n in nodes:',
    // D26: a local stored inside a loop / branch that reads a tainted name, or the receiver of a
    // method call fed a tainted argument (`out.append(c)`), is tainted too.
    '            fresh = []',
    '            if isinstance(n, (ast.For, ast.AsyncFor, ast.If, ast.While)):',
    '                gate = n.iter if isinstance(n, (ast.For, ast.AsyncFor)) else n.test',
    '                if names_in(gate, t):',
    '                    for sub in sub_nodes(n.body + n.orelse):',
    '                        if isinstance(sub, ast.Name) and not isinstance(sub.ctx, ast.Load):',
    '                            fresh.append(sub.id)',
    '            elif isinstance(n, ast.Call) and isinstance(n.func, ast.Attribute) and isinstance(n.func.value, ast.Name):',
    '                if any(names_in(a, t) for a in list(n.args) + [k.value for k in n.keywords]):',
    '                    fresh.append(n.func.value.id)',
    '            for nm in fresh:',
    '                if nm not in t:',
    '                    t.add(nm)',
    '                    changed = True',
    '            if isinstance(n, ast.Assign):',
    '                pairs = [(tg, n.value) for tg in n.targets]',
    '            elif isinstance(n, (ast.AnnAssign, ast.AugAssign)):',
    '                pairs = [(n.target, n.value)]',
    '            elif isinstance(n, (ast.For, ast.AsyncFor, ast.comprehension)):',
    '                pairs = [(n.target, n.iter)]',
    '            elif isinstance(n, ast.withitem):',
    '                pairs = [(n.optional_vars, n.context_expr)] if n.optional_vars is not None else []',
    '            elif hasattr(ast, "NamedExpr") and isinstance(n, ast.NamedExpr):',
    '                pairs = [(n.target, n.value)]',
    '            else:',
    '                continue',
    '            for tg, val in pairs:',
    '                if names_in(val, t):',
    '                    out = []',
    '                    targets(tg, out)',
    '                    for nm in out:',
    '                        if nm not in t:',
    '                            t.add(nm)',
    '                            changed = True',
    '    return t',
    '',
    // An "edge": an ordering comparison or a modulo whose operands involve a tainted name.
    'def has_edge(fn):',
    '    t = tainted_names(fn)',
    '    for n in own_nodes(fn):',
    '        if isinstance(n, ast.Compare):',
    '            if any(isinstance(o, (ast.Lt, ast.LtE, ast.Gt, ast.GtE)) for o in n.ops) and any(names_in(e, t) for e in [n.left] + list(n.comparators)):',
    '                return True',
    '        elif isinstance(n, ast.BinOp) and isinstance(n.op, ast.Mod):',
    '            if names_in(n.left, t) or names_in(n.right, t):',
    '                return True',
    '        elif isinstance(n, ast.AugAssign) and isinstance(n.op, ast.Mod):',
    '            if names_in(n.value, t) or names_in(n.target, t):',
    '                return True',
    '    return False',
    '',
    // (direct input call, direct other-file call, same-file callee names) for one function.
    'def call_facts(fn, own_top, mods):',
    '    loc = param_names(fn)',
    '    for n in own_nodes(fn):',
    '        if isinstance(n, ast.Name) and not isinstance(n.ctx, ast.Load):',
    '            loc.add(n.id)',
    '    uses_input, uses_other, same = False, False, set()',
    '    for n in ast.walk(fn):',
    '        if not isinstance(n, ast.Call):',
    '            continue',
    '        f = n.func',
    '        if isinstance(f, ast.Name):',
    '            if f.id in loc:',
    '                continue',
    '            if f.id == "input":',
    '                uses_input = True',
    '            elif f.id in own_top:',
    '                same.add(f.id)',
    '            elif f.id in OTHER_DEFS:',
    '                uses_other = True',
    '        elif isinstance(f, ast.Attribute) and isinstance(f.value, ast.Name) and f.value.id in mods and f.value.id not in loc:',
    '            uses_other = True',
    '    return (uses_input, uses_other, same)',
    '',
    'def game_uses(fn):',
    '    a = fn.args',
    '    params = set(x.arg for x in getattr(a, "posonlyargs", []) + a.args + a.kwonlyargs)',
    '    if a.vararg:',
    '        params.add(a.vararg.arg)',
    '    if a.kwarg:',
    '        params.add(a.kwarg.arg)',
    '    glob, local, loads = set(), set(), []',
    '    for stmt in fn.body:',
    '        for n in ast.walk(stmt):',
    '            if isinstance(n, ast.Global):',
    '                glob.update(n.names)',
    '            elif isinstance(n, ast.Name):',
    '                if isinstance(n.ctx, ast.Load):',
    '                    loads.append(n)',
    '                else:',
    '                    local.add(n.id)',
    '            elif isinstance(n, (ast.FunctionDef, ast.AsyncFunctionDef, ast.ClassDef)):',
    '                local.add(n.name)',
    '            elif isinstance(n, ast.arg):',
    '                local.add(n.arg)',
    '            elif isinstance(n, (ast.Import, ast.ImportFrom)):',
    '                for al in n.names:',
    '                    if al.name != "*":',
    '                        local.add(al.asname or al.name.split(".")[0])',
    '            elif isinstance(n, ast.ExceptHandler) and n.name:',
    '                local.add(n.name)',
    // a `match` capture (`case {"k": screen}`, `case [*rest]`, `case {**rest}`) binds a local
    '            elif isinstance(n, MATCH_BINDS):',
    '                for nm in (getattr(n, "name", None), getattr(n, "rest", None)):',
    '                    if nm:',
    '                        local.add(nm)',
    '    local -= glob',
    '    loads.sort(key=lambda n: (n.lineno, n.col_offset))',
    '    out = []',
    '    for n in loads:',
    '        if n.id in GAME and n.id not in params and n.id not in local and n.id not in out:',
    '            out.append(n.id)',
    '    return out',
    '',
    'try:',
    '    tree = ast.parse(src)',
    'except SyntaxError as e:',
    '    print(' + JSON.stringify(FUNCTION_LIST_SENTINEL) + ' + json.dumps({"error": "syntax", "line": e.lineno or 0}))',
    'else:',
    '    if PG:',
    '        collect_game(tree.body)',
    '    found = {}',
    '    for node in tree.body:',
    '        if not isinstance(node, (ast.FunctionDef, ast.AsyncFunctionDef)):',
    '            continue',
    '        found.pop(node.name, None)',
    '        if node.name in HOOKS:',
    '            continue',
    '        a = node.args',
    '        params = [x.arg for x in getattr(a, "posonlyargs", [])] + [x.arg for x in a.args]',
    '        if not params:',
    '            continue',
    '        seg = ast.get_source_segment(src, node) or ""',
    '        found[node.name] = {"name": node.name, "params": params, "line": node.lineno, "source": seg[:' + MAX_FUNCTION_SOURCE + ']}',
    '        if PG:',
    '            found[node.name]["gameUses"] = game_uses(node)',
    '    hidden = []',
    '    if HIDE:',
    '        defs, own_top, mods = {}, set(), set()',
    '        for node in tree.body:',
    '            if isinstance(node, (ast.FunctionDef, ast.AsyncFunctionDef)):',
    '                defs[node.name] = node',
    '            if isinstance(node, (ast.FunctionDef, ast.AsyncFunctionDef, ast.ClassDef)):',
    '                own_top.add(node.name)',
    '        if OTHER_STEM:',
    '            for node in ast.walk(tree):',
    '                if isinstance(node, ast.Import):',
    '                    for al in node.names:',
    '                        if al.name == OTHER_STEM:',
    '                            mods.add(al.asname or al.name)',
    '        facts = dict((k, call_facts(v, own_top, mods)) for k, v in defs.items())',
    '        reach_in = dict((k, f[0]) for k, f in facts.items())',
    '        reach_imp = dict((k, f[1]) for k, f in facts.items())',
    '        changed = True',
    '        while changed:',
    '            changed = False',
    '            for k, f in facts.items():',
    '                for c in f[2]:',
    '                    if reach_in.get(c) and not reach_in[k]:',
    '                        reach_in[k] = True',
    '                        changed = True',
    '                    if reach_imp.get(c) and not reach_imp[k]:',
    '                        reach_imp[k] = True',
    '                        changed = True',
    '        for name in list(found.keys()):',
    '            if reach_in.get(name):',
    '                reason = "input"',
    '            elif reach_imp.get(name):',
    '                reason = "imports"',
    '            elif not has_edge(defs[name]):',
    '                reason = "edge"',
    '            else:',
    '                continue',
    '            del found[name]',
    '            hidden.append({"name": name, "reason": reason})',
    '    print(' + JSON.stringify(FUNCTION_LIST_SENTINEL) + ' + json.dumps({"functions": list(found.values()), "hidden": hidden}))',
    ''
  ].join('\n');
}

/**
 * Read the function-list program's answer back:
 *   { functions: [{ name, params, line, source, gameUses? }], hidden: [{ name, reason }] }
 *   | { error: 'syntax', line } | null
 * (no answer). `hidden` is always an array (validated: name <= 100 chars, max 50, reason one of
 * input / edge / imports). `gameUses` is present only when the program sent it (a pgzero host).
 */
export function readFunctionList(output) {
  var text = output == null ? '' : String(output);
  var at = text.lastIndexOf(FUNCTION_LIST_SENTINEL);
  if (at === -1) { return null; }
  var line = text.slice(at + FUNCTION_LIST_SENTINEL.length).split('\n')[0];
  var parsed;
  try { parsed = JSON.parse(line); } catch (e) { return null; }
  if (!parsed || typeof parsed !== 'object') { return null; }
  if (parsed.error === 'syntax') {
    return { error: 'syntax', line: (typeof parsed.line === 'number' && parsed.line > 0) ? parsed.line : 0 };
  }
  if (!Array.isArray(parsed.functions)) { return null; }
  var hiddenOut = (Array.isArray(parsed.hidden) ? parsed.hidden : []).filter(function (h) {
    return h && typeof h.name === 'string' && h.name && HIDDEN_REASONS.indexOf(h.reason) !== -1;
  }).slice(0, MAX_HIDDEN).map(function (h) { return { name: h.name.slice(0, WIRE_MAX_NAME), reason: h.reason }; });
  return {
    hidden: hiddenOut,
    functions: parsed.functions.filter(function (f) {
      return f && typeof f.name === 'string' && Array.isArray(f.params) && f.params.length > 0;
    }).map(function (f) {
      var out = {
        name: f.name,
        params: f.params.map(function (p) { return String(p); }),
        line: typeof f.line === 'number' ? f.line : 0,
        source: String(f.source || '').slice(0, MAX_FUNCTION_SOURCE)
      };
      // pgzero hosts only: the game objects the function reads (strings, ≤ 10 × 100 chars).
      if (Array.isArray(f.gameUses)) {
        out.gameUses = f.gameUses.filter(function (g) { return typeof g === 'string' && g; })
          .slice(0, MAX_GAME_USES).map(function (g) { return g.slice(0, WIRE_MAX_NAME); });
      }
      return out;
    })
  };
}

/** The chooser's option text: `name(a, b)`. */
export function functionSignature(fn) {
  if (!fn || typeof fn.name !== 'string') { return ''; }
  return fn.name + '(' + (Array.isArray(fn.params) ? fn.params.join(', ') : '') + ')';
}

/** Is this observation a game-only raise (`_PcGameOnly`)? Detected by exception TYPE. */
export function isGameOnly(obs) {
  return !!(obs && obs.kind === 'error' && obs.type === GAME_ONLY_TYPE);
}

/** The object name in a `_PcGameOnly: <name>` text (module prefix tolerated), or ''. */
export function gameOnlyName(text) {
  var m = /(?:^|\.)_PcGameOnly:\s*(\S+)/.exec(String(text == null ? '' : text));
  return m ? m[1] : '';
}

/** The wire `actualOutput` / widget actual for a GAME_ONLY row. */
export function gameOnlyActual(name) {
  return "Can't test: uses " + (name || 'a game object');
}

var IDENT_RE = /^[A-Za-z_]\w*$/;

/**
 * The client mirror of the server's own-mode completeness rule (`lib/util/test-table.js`
 * `ownCompleteFromParts`) over WIRE-shaped rows (`{type}`) and results (`{rowIndex, pass}`).
 * `minRows` is floored at 1 per type here too.
 */
export function ownCompleteFor(rows, results, minRows, functionName) {
  var name = typeof functionName === 'string' ? functionName.trim() : '';
  if (!IDENT_RE.test(name)) { return false; }
  var list = Array.isArray(rows) ? rows : [];
  var counts = { expected: 0, boundary: 0, invalid: 0 };
  list.forEach(function (r) {
    var t = r && r.type;
    if (t === 'expected' || t === 'boundary' || t === 'invalid') { counts[t] += 1; }
  });
  var mins = minRows || {};
  var enough = ROW_TYPES.every(function (k) {
    var m = (typeof mins[k] === 'number' && mins[k] >= 1) ? mins[k] : 1;
    return counts[k] >= m;
  });
  if (!enough) { return false; }
  var verdict = {};
  (Array.isArray(results) ? results : []).forEach(function (res) {
    if (!res || !Number.isInteger(res.rowIndex)) { return; }
    var ok = res.pass === true;
    verdict[res.rowIndex] = (verdict[res.rowIndex] === undefined) ? ok : (verdict[res.rowIndex] && ok);
  });
  for (var i = 0; i < list.length; i++) {
    if (verdict[i] !== true) { return false; }
  }
  return true;
}

/** Own mode: does this row EXPECT an error? (Invalid rows, unless explicitly "It should return:"). */
function ownExpectsError(row) {
  return rowType(row) === 'invalid' && !(row && row.expectError === false);
}

var HARD_MAX_ROWS = 30;
var FLOAT_EPS = 1e-9;

// The `testTableRun` route's Joi caps (config/api_routes.js) — `toWire` truncates to these.
export var WIRE_MAX_CELL_CHARS = 2000;
var WIRE_MAX_ROWS = 30;
var WIRE_MAX_MUTANTS = 50;
var WIRE_MAX_FINGERPRINT = 100;
function cap(s) { return s.length > WIRE_MAX_CELL_CHARS ? s.slice(0, WIRE_MAX_CELL_CHARS) : s; }

function num(v) { return typeof v === 'number' && isFinite(v); }

export function rowType(row) {
  return (row && ROW_TYPES.indexOf(row.type) !== -1) ? row.type : 'expected';
}

/** How many input cells a row carries for this configuration. */
export function inputCount(cfg) {
  var c = cfg || {};
  if (c.target === 'stdin') { return Math.max(1, Math.min(10, c.inputCount || 1)); }
  var n = Array.isArray(c.params) ? c.params.length : 0;
  return n;
}

/** The compare mode a row uses: a stdin target always compares printed text. */
export function compareMode(cfg) {
  var c = cfg || {};
  if (c.target === 'stdin') { return 'stdout'; }
  return c.compare === 'stdout' ? 'stdout' : 'return';
}

/** A fresh, empty row for this configuration. */
export function blankRow(cfg, type) {
  var inputs = [];
  for (var i = 0; i < inputCount(cfg); i++) { inputs.push(''); }
  return { type: ROW_TYPES.indexOf(type) !== -1 ? type : 'expected', inputs: inputs, predicted: '' };
}

/**
 * Split a stored row's `input` (the wire joins its cells with ', ') back into `count` cells, for
 * resuming the table on reload. Top-level commas only: a comma inside quotes, brackets or braces
 * belongs to a literal (`"a, b"`, `[1, 2]`). A single-cell row is returned whole.
 */
export function splitInputs(text, count) {
  var s = String(text == null ? '' : text);
  var n = Math.max(0, count | 0);
  if (n <= 1) { return n === 1 ? [s] : []; }
  var cells = [], cur = '', depth = 0, quote = '';
  for (var i = 0; i < s.length; i++) {
    var ch = s[i];
    if (quote) {
      cur += ch;
      if (ch === '\\' && i + 1 < s.length) { cur += s[++i]; continue; }
      if (ch === quote) { quote = ''; }
      continue;
    }
    if (ch === '"' || ch === "'") { quote = ch; cur += ch; continue; }
    if (ch === '[' || ch === '(' || ch === '{') { depth++; }
    if (ch === ']' || ch === ')' || ch === '}') { depth = Math.max(0, depth - 1); }
    if (ch === ',' && depth === 0 && cells.length < n - 1) {
      cells.push(cur.trim());
      cur = '';
      continue;
    }
    cur += ch;
  }
  cells.push(cur.trim());
  while (cells.length < n) { cells.push(''); }
  return cells;
}

/** Rebuild the widget's rows from a stored response's wire rows. */
export function rowsFromSaved(savedRows, cfg) {
  var n = inputCount(cfg);
  var own = !!(cfg && cfg.ownFunction === true);
  return (Array.isArray(savedRows) ? savedRows : []).map(function (r) {
    var type = ROW_TYPES.indexOf(r && r.type) !== -1 ? r.type : 'expected';
    var out = {
      type: type,
      inputs: splitInputs(r && r.input, n),
      predicted: type === 'invalid' ? '' : String((r && r.predictedOutput) || '')
    };
    // pgzero-test-tables (4.5b): an own-mode Invalid row keeps its raise/return choice, and its
    // saved prediction when it is "It should return:".
    if (own && type === 'invalid') {
      out.expectError = !(r && r.expectError === false);
      if (!out.expectError) { out.predicted = String((r && r.predictedOutput) || ''); }
    }
    return out;
  });
}

/** The HARD cap on rows (authored `maxRows`, never above 30). */
export function maxRowsFor(cfg) {
  var m = cfg && cfg.maxRows;
  return (typeof m === 'number' && m >= 1) ? Math.min(Math.floor(m), HARD_MAX_ROWS) : HARD_MAX_ROWS;
}

export function canAddRow(rows, cfg) {
  return (Array.isArray(rows) ? rows.length : 0) < maxRowsFor(cfg);
}

/** ADVISORY: indexes of rows whose inputs repeat an EARLIER row's (trimmed, cell by cell). */
export function duplicateRows(rows) {
  var seen = {}, dups = [];
  (Array.isArray(rows) ? rows : []).forEach(function (row, i) {
    var key = JSON.stringify(((row && row.inputs) || []).map(function (v) { return String(v == null ? '' : v).trim(); }));
    if (Object.prototype.hasOwnProperty.call(seen, key)) { dups.push(i); } else { seen[key] = i; }
  });
  return dups;
}

/** ADVISORY coverage against `minRows` (find-the-bug's rule, shared). Never gates. */
export function coverage(rows, cfg) {
  return ftbCoverage(rows, cfg);
}

/**
 * Every raw literal the rows need parsed, in ONE list, plus a per-row map back into it:
 * each row's input cells, then (return compare, not an Invalid row) its prediction.
 */
export function parsePlan(rows, cfg) {
  var raw = [], cmp = compareMode(cfg), n = inputCount(cfg);
  var own = !!(cfg && cfg.ownFunction === true);
  var map = (Array.isArray(rows) ? rows : []).map(function (row) {
    var entry = { inputs: [], predicted: -1 };
    var cells = (row && Array.isArray(row.inputs)) ? row.inputs : [];
    for (var i = 0; i < n; i++) {
      entry.inputs.push(raw.length);
      raw.push(typeof cells[i] === 'string' ? cells[i] : '');
    }
    // An own-mode Invalid row set to "It should return:" carries a prediction too (4.5b).
    var expectsError = own ? ownExpectsError(row) : rowType(row) === 'invalid';
    if (cmp === 'return' && !expectsError) {
      entry.predicted = raw.length;
      raw.push(typeof row.predicted === 'string' ? row.predicted : '');
    }
    return entry;
  });
  return { raw: raw, map: map };
}

/**
 * Resolve each row against the parse output: its call arguments (function target — JSON values),
 * its stdin lines (Python's own `str()` of each literal), its expectation, or `badLiteral`.
 */
export function resolveRows(rows, cfg, plan, parsed) {
  var target = (cfg && cfg.target) === 'stdin' ? 'stdin' : 'function';
  var own = !!(cfg && cfg.ownFunction === true);
  // WRAPPED mode (own-function mode on either host, or any pgzero host): a row whose INPUTS are
  // fine but whose PREDICTION is empty/invalid is still CALLED (`badPrediction`), so a game-only
  // raise shows "Can't test" straight away instead of hiding behind "check the prediction"
  // (owner report 2026-10-04). Python author mode keeps the old whole-row `badLiteral`.
  var wrapped = own || (cfg && cfg.lang) === 'pgzero';
  return (Array.isArray(rows) ? rows : []).map(function (row, i) {
    var m = plan.map[i];
    var ins = m.inputs.map(function (k) { return parsed ? parsed[k] : null; });
    var pred = (m.predicted >= 0 && parsed) ? parsed[m.predicted] : null;
    var badInput = !parsed
      || ins.some(function (p) { return !p || !p.ok || (target === 'function' && !p.hasValue); });
    var badPred = m.predicted >= 0 && (!pred || !pred.ok || !pred.hasValue);
    if (badInput || (badPred && !wrapped)) { return { badLiteral: true }; }
    if (badPred) {
      return {
        badLiteral: false,
        badPrediction: true,
        badCell: 'prediction',
        args: ins.map(function (p) { return p.value; }),
        stdin: ins.map(function (p) { return p.str; }),
        expectation: null
      };
    }
    return {
      badLiteral: false,
      args: ins.map(function (p) { return p.value; }),
      stdin: ins.map(function (p) { return p.str; }),
      expectation: {
        // own mode honours the row's raise/return choice; author mode keeps today's rule.
        expectError: own ? ownExpectsError(row) : rowType(row) === 'invalid',
        value: pred ? pred.value : undefined,
        valueIsFloat: !!(pred && pred.pyType === 'float'),
        text: typeof row.predicted === 'string' ? row.predicted : ''
      }
    };
  });
}

function tolerance(cfg) {
  var t = cfg && cfg.boundaryTolerance;
  return (typeof t === 'number' && isFinite(t) && t >= 0) ? t : 0;
}

/** Deep value equality, with numbers equal within `tol` (never below float noise). */
export function valuesClose(a, b, tol) {
  var t = Math.max(num(tol) ? tol : 0, FLOAT_EPS);
  if (num(a) && num(b)) { return Math.abs(a - b) <= t; }
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) { return false; }
    for (var i = 0; i < a.length; i++) { if (!valuesClose(a[i], b[i], tol)) { return false; } }
    return true;
  }
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    var ka = Object.keys(a).sort(), kb = Object.keys(b).sort();
    if (ka.length !== kb.length) { return false; }
    for (var j = 0; j < ka.length; j++) {
      if (ka[j] !== kb[j] || !valuesClose(a[ka[j]], b[kb[j]], tol)) { return false; }
    }
    return true;
  }
  return a === b;
}

/**
 * RULE 2 — the student's OWN row verdict: does their code do what they predicted? Never reads the
 * model answer. `boundaryTolerance` applies to a numeric Expected/Boundary prediction only.
 */
export function gradeOwnRow(opts) {
  var o = opts || {};
  if (o.badLiteral) { return OWN.BAD_LITERAL; }
  // A row CALLED without a usable prediction (wrapped mode): only a game-only raise outranks
  // the prediction problem — never a PASS / FAIL against a missing prediction.
  if (o.badPrediction) { return isGameOnly(o.observed) ? OWN.GAME_ONLY : OWN.BAD_LITERAL; }
  var obs = o.observed;
  if (!obs) { return OWN.NOT_RUN; }
  // A game-only raise is checked by exception TYPE before any other rule — never a pass, not
  // even on a row that expects an error.
  if (isGameOnly(obs)) { return OWN.GAME_ONLY; }
  if (obs.kind === 'timeout') { return OWN.TIMEOUT; }
  var exp = o.expectation || {};
  if (exp.expectError) { return obs.kind === 'error' ? OWN.PASS : OWN.FAIL; }
  if (o.compare === 'return' && obs.kind === 'value' && obs.jsonSafe !== false
      && num(obs.value) && num(exp.value) && tolerance(o.cfg) > 0) {
    return valuesClose(obs.value, exp.value, tolerance(o.cfg)) ? OWN.PASS : OWN.FAIL;
  }
  return matchesExpectation(obs, exp, o.compare) ? OWN.PASS : OWN.FAIL;
}

/** Do two hidden-runner observations BEHAVE the same on one input? (rule 3) */
export function sameBehaviour(a, b, compare, tol) {
  if (!a || !b) { return false; }
  if (a.kind !== b.kind) { return false; }
  if (a.kind === 'error' || a.kind === 'missing' || a.kind === 'timeout') { return true; }
  if (compare === 'return') {
    if (a.jsonSafe === false || b.jsonSafe === false) { return String(a.value) === String(b.value); }
    return valuesClose(a.value, b.value, tol);
  }
  return normalizeOutput(a.stdout, {}) === normalizeOutput(b.stdout, {});
}

/** RULE 3 — does THIS row catch THIS mutant? */
export function rowCatchesMutant(type, model, mutant, compare, tol) {
  if (!model || !mutant) { return false; }
  // An unusable row: the model answer itself did not finish or has no such function.
  if (model.kind === 'timeout' || model.kind === 'missing') { return false; }
  // pgzero-test-tables (D4): a game-only raise from EITHER side makes this row × source
  // unusable — no catch credit, like a model timeout.
  if (isGameOnly(model) || isGameOnly(mutant)) { return false; }
  if (type === 'invalid') {
    return model.kind === 'error' && mutant.kind !== 'error';
  }
  return !sameBehaviour(model, mutant, compare, tol);
}

/**
 * The caught-set of ONE run: `modelObs[r]` is the model answer's observation on row r,
 * `mutantObs[m][r]` mutant m's. Bad-literal rows (`null` observations) catch nothing.
 */
export function caughtMutants(rows, modelObs, mutantObs, cfg) {
  var cmp = compareMode(cfg), tol = tolerance(cfg), caught = [];
  (Array.isArray(mutantObs) ? mutantObs : []).forEach(function (obsList, m) {
    var hit = (Array.isArray(rows) ? rows : []).some(function (row, r) {
      return rowCatchesMutant(rowType(row), modelObs[r], obsList && obsList[r], cmp, tol);
    });
    if (hit) { caught.push(m); }
  });
  return caught;
}

function normaliseSet(list) {
  var seen = {}, out = [];
  (Array.isArray(list) ? list : []).forEach(function (n) {
    if (typeof n === 'number' && Number.isInteger(n) && n >= 0 && !seen[n]) { seen[n] = true; out.push(n); }
  });
  return out.sort(function (a, b) { return a - b; });
}

/** RULE 4 — sticky WITHIN a fingerprint, a fresh start ACROSS one. Mirrors the server's helper. */
export function stickyMutantsCaught(previous, next, previousFingerprint, currentFingerprint) {
  if (!previousFingerprint || previousFingerprint !== currentFingerprint) { return normaliseSet(next); }
  return normaliseSet((Array.isArray(previous) ? previous : []).concat(Array.isArray(next) ? next : []));
}

/** The required-mutant tally the widget and the gating UI read. */
export function requiredStatus(mutants, caught) {
  var req = [], set = normaliseSet(caught);
  (Array.isArray(mutants) ? mutants : []).forEach(function (m, i) { if (m && m.required === true) { req.push(i); } });
  var got = req.filter(function (i) { return set.indexOf(i) !== -1; }).length;
  return {
    total: (Array.isArray(mutants) ? mutants.length : 0),
    caught: set.length,
    required: req.length,
    requiredCaught: got,
    allRequiredCaught: got === req.length
  };
}

/** Friendly Result-column label for an OWN verdict. Never names a correct value. */
export function ownLabel(result) {
  switch (result) {
    case OWN.PASS: return 'Pass';
    case OWN.FAIL: return 'Fail';
    case OWN.TIMEOUT: return 'Did not finish';
    case OWN.BAD_LITERAL: return 'Check the input';
    case OWN.GAME_ONLY: return "Can't test";
    default: return '—';
  }
}

/**
 * The wire shape POSTed to `testTableRun` — the ONE place the client builds it. Never carries a
 * mutant's or the model answer's code or output: only the student's own rows, their own code's
 * output and their own pass flag. The server whitelist-rebuilds it anyway.
 */
export function toWire(opts) {
  var o = opts || {};
  var target = (o.cfg && o.cfg.target) === 'stdin' ? 'stdin' : 'function';
  // Every cap below MIRRORS the `testTableRun` route's Joi schema (config/api_routes.js). This
  // repo's validator SKIPS the handler and answers a benign 200 on ANY Joi error, so one
  // over-long output would silently drop the whole Run — truncate here, never rely on the server.
  var own = !!(o.cfg && o.cfg.ownFunction === true);
  var rows = (Array.isArray(o.rows) ? o.rows : []).slice(0, WIRE_MAX_ROWS).map(function (row) {
    var invalid = rowType(row) === 'invalid';
    // Own-mode Invalid "It should return:" sends its real prediction (4.5b).
    var returns = own && invalid && row && row.expectError === false;
    var out = {
      target: target,
      input: cap(((row && row.inputs) || []).map(function (v) { return String(v == null ? '' : v); }).join(', ')),
      type: rowType(row),
      predictedOutput: (invalid && !returns) ? '(raises an error)' : cap(String((row && row.predicted) || ''))
    };
    if (own && invalid) { out.expectError = !returns; }
    return out;
  });
  var results = (Array.isArray(o.results) ? o.results : []).slice(0, WIRE_MAX_ROWS).map(function (res, i) {
    var out = { rowIndex: i, actualOutput: cap(String((res && res.actual) || '')), pass: !!(res && res.own === OWN.PASS) };
    // pgzero-test-tables (U10): the two non-verdict outcomes are stored as a `verdict`.
    if (res && res.own === OWN.GAME_ONLY) {
      out.verdict = 'game-only';
      out.actualOutput = cap(gameOnlyActual(res.gameOnly));
      out.pass = false;
    } else if (res && res.own === OWN.TIMEOUT) {
      out.verdict = 'timeout';
      out.actualOutput = 'Did not finish';
      out.pass = false;
    }
    return out;
  });
  var wire = {
    parent: String(o.parent || ''),
    fingerprint: String((o.cfg && o.cfg.fingerprint) || '').slice(0, WIRE_MAX_FINGERPRINT),
    rows: rows,
    results: results,
    mutantsCaught: own ? [] : normaliseSet(o.mutantsCaught).filter(function (n) { return n < WIRE_MAX_MUTANTS; })
  };
  if (own) {
    // The chosen function (the EFFECTIVE config) and the source that was tested — truncated to the
    // route's Joi caps, since a Joi error would silently skip the save.
    wire.functionName = String((o.cfg && o.cfg.functionName) || '').slice(0, WIRE_MAX_NAME);
    wire.params = (Array.isArray(o.cfg && o.cfg.params) ? o.cfg.params : []).slice(0, WIRE_MAX_PARAMS)
      .map(function (p) { return String(p == null ? '' : p).slice(0, WIRE_MAX_NAME); });
    wire.functionSource = String(o.functionSource || '').slice(0, MAX_FUNCTION_SOURCE);
  }
  // guided-make ONLY: its working copy carries no class stamp, so the server ships the launch
  // URL's class in the config and the write needs it back (verified server-side). The server
  // never sets it for assignment / challenge, whose class is their working copy's own stamp.
  if (o.cfg && o.cfg.linkedCourseId) { wire.linkedCourseId = String(o.cfg.linkedCourseId); }
  return wire;
}

/**
 * Run a whole suite. Runners are INJECTED so this orchestration is testable with fakes:
 *   parse(rawList)            -> Promise<parsed[] | null>       (one run of the parse program)
 *   own(resolvedRow)          -> Promise<observation>           (the STUDENT's code, visible runner)
 *   model(resolvedRow)        -> Promise<observation>           (the model answer, hidden runner)
 *   useSource(src)            -> Promise                        (swap the hidden program)
 *   mutant(resolvedRow)       -> Promise<observation>           (the loaded mutant, hidden runner)
 *   progress(text)            (optional)
 * pgzero-test-tables (4.5a) options — absent, the behaviour is byte-identical to before:
 *   ownSource (string|null)   `useSource(ownSource)` BEFORE the own phase (the own calls then hit
 *                             the wrapped student source in the hidden runner)
 *   ownOnly (boolean)         skip the model AND mutant phases entirely (own-function mode)
 *   wrap (src -> src)         applied to the model source and every mutant before `useSource`
 * One call per (row × source), strictly sequential. Resolves with
 *   { rows: [{ own, actual, gameOnly?, badCell? }], caught: [mutantIndex], ok: true } or { ok: false, reason }.
 * `badCell: 'prediction'` marks a BAD_LITERAL row whose inputs ran but whose prediction is unusable.
 */
export function runSuite(opts) {
  var o = opts || {};
  var cfg = o.cfg || {};
  var rows = Array.isArray(o.rows) ? o.rows : [];
  var mutants = Array.isArray(cfg.mutants) ? cfg.mutants : [];
  var cmp = compareMode(cfg);
  var say = typeof o.progress === 'function' ? o.progress : function () {};
  var plan = parsePlan(rows, cfg);
  var wrap = typeof o.wrap === 'function' ? o.wrap : function (src) { return src; };
  var resolved;

  function seq(list, fn) {
    var out = [];
    return list.reduce(function (p, item, i) {
      return p.then(function () { return fn(item, i); }).then(function (v) { out.push(v); });
    }, Promise.resolve()).then(function () { return out; });
  }

  say('Reading your inputs…');
  return Promise.resolve(plan.raw.length ? o.parse(plan.raw) : []).then(function (parsed) {
    if (plan.raw.length && !parsed) { return { ok: false, reason: 'parse-failed' }; }
    resolved = resolveRows(rows, cfg, plan, parsed);
    var loadOwn = (typeof o.ownSource === 'string') ? Promise.resolve(o.useSource(o.ownSource)) : Promise.resolve();
    return loadOwn.then(function () {
      return seq(resolved, function (r, i) {
        if (r.badLiteral) { return { own: OWN.BAD_LITERAL, actual: '', observed: null }; }
        say('Running test ' + (i + 1) + ' of ' + rows.length + '…');
        return o.own(r).then(function (obs) {
          var verdict = gradeOwnRow({ observed: obs, expectation: r.expectation, compare: cmp, cfg: cfg,
            badPrediction: r.badPrediction === true });
          if (verdict === OWN.GAME_ONLY) {
            // Never formatObserved's "Error: _PcGameOnly" — name the object instead.
            var name = gameOnlyName(obs && obs.text);
            return { own: verdict, actual: gameOnlyActual(name), gameOnly: name, observed: obs };
          }
          if (r.badPrediction) {
            // The inputs ran; only the prediction needs fixing ("Check what it returns").
            return { own: OWN.BAD_LITERAL, actual: '', badCell: 'prediction', observed: obs };
          }
          return {
            own: verdict,
            actual: formatObserved(obs, cmp),
            observed: obs
          };
        });
      });
    }).then(function (ownResults) {
      function strip(x) {
        var out = { own: x.own, actual: x.actual };
        if (x.own === OWN.GAME_ONLY) { out.gameOnly = x.gameOnly || ''; }
        if (x.badCell) { out.badCell = x.badCell; }
        return out;
      }
      if (o.ownOnly === true) {
        return { ok: true, rows: ownResults.map(strip), caught: [] };
      }
      say('Checking your tests against the hidden versions…');
      // The hidden runner may still hold the last mutant from a previous run: load the model
      // answer first, every time.
      return Promise.resolve(o.useSource(wrap(cfg.modelSource || ''))).then(function () {
        // A row with no usable prediction earns no mutant credit: skipped like a bad literal.
        return seq(resolved, function (r) { return (r.badLiteral || r.badPrediction) ? null : o.model(r); });
      }).then(function (modelObs) {
        return seq(mutants, function (mut, m) {
          say('Trying your tests on hidden faulty copy ' + (m + 1) + ' of ' + mutants.length + '…');
          return Promise.resolve(o.useSource(wrap(mut.code || ''))).then(function () {
            return seq(resolved, function (r) { return (r.badLiteral || r.badPrediction) ? null : o.mutant(r); });
          });
        }).then(function (mutantObs) {
          var cmpMode = compareMode(cfg), tol = tolerance(cfg), evidence = {};
          // Display only: the FIRST row that catches each faulty copy, with both answers.
          mutantObs.forEach(function (obsList, m) {
            for (var r = 0; r < rows.length; r++) {
              if (rowCatchesMutant(rowType(rows[r]), modelObs[r], obsList && obsList[r], cmpMode, tol)) {
                evidence[m] = { row: r, real: formatObserved(modelObs[r], cmpMode), faulty: formatObserved(obsList[r], cmpMode) };
                break;
              }
            }
          });
          return {
            ok: true,
            rows: ownResults.map(strip),
            caught: caughtMutants(rows, modelObs, mutantObs, cfg),
            evidence: evidence
          };
        });
      });
    });
  });
}
